import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import type { SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { getSearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import { LoomFolderIcon } from '../../../../../quick-find/dialog/loom-folder-icon';
import { LoomPlaylistIcon } from '../../../../../quick-find/dialog/loom-playlist-icon';
import { LoomSpaceIcon } from '../../../../../quick-find/dialog/loom-space-icon';
import { LoomTagIcon } from '../../../../../quick-find/dialog/loom-tag-icon';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchLoomEntity_AtlassianSearchPage$key } from './__generated__/searchLoomEntity_AtlassianSearchPage.graphql';
import { messages } from './messages';

const styles = cssMap({
	iconContainer: {
		height: '32px',
		width: '32px',
	},
});

const LOOM_ENTITY_TYPENAMES = new Set([
	'SearchResultLoomFolder',
	'SearchResultLoomPlaylist',
	'SearchResultLoomSpace',
	'SearchResultLoomTag',
]);

export const isSearchLoomEntity = (node: { __typename?: string }): boolean =>
	!!node.__typename && LOOM_ENTITY_TYPENAMES.has(node.__typename);

const getIcon = (typename: string, title: string): React.JSX.Element => {
	let icon: React.JSX.Element;

	switch (typename) {
		case 'SearchResultLoomFolder':
			icon = <LoomFolderIcon />;
			break;
		case 'SearchResultLoomPlaylist':
			icon = <LoomPlaylistIcon size="medium" />;
			break;
		case 'SearchResultLoomSpace':
			icon = <LoomSpaceIcon name={title} />;
			break;
		case 'SearchResultLoomTag':
			icon = <LoomTagIcon />;
			break;
		default:
			icon = <LoomFolderIcon />;
	}

	return <Box xcss={styles.iconContainer}>{icon}</Box>;
};

export const SearchLoomEntity = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchResultProps<searchLoomEntity_AtlassianSearchPage$key> &
	ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchLoomEntity_AtlassianSearchPage on SearchResult {
				__typename
				id
				title
				type
				url
				... on SearchResultLoomFolder {
					totalNestedVideos
				}
				... on SearchResultLoomPlaylist {
					creatorName
				}
				... on SearchResultLoomTag {
					count
				}
			}
		`,
		node,
	);
	const { formatMessage } = useIntl();
	const [, , , contentId] = useMemo(() => getProductAndIDFromARI(data.id), [data.id]);
	const metadata = useMemo(
		() =>
			({
				id: data.id,
				containerId: null,
				spaceId: null,
				contentType: data.type,
				entityType: data.type,
				contentId,
				product: ProductKeys.Loom,
				...getSiteMetadata(data.id, cloudId, selectedSites),
			}) satisfies AdvancedSearchResultInteractedMetadata,
		[cloudId, contentId, data.id, data.type, selectedSites],
	);

	const videoCount =
		data.__typename === 'SearchResultLoomTag'
			? data.count
			: data.__typename === 'SearchResultLoomFolder'
				? data.totalNestedVideos
				: null;
	const creatorName = data.__typename === 'SearchResultLoomPlaylist' ? data.creatorName : null;

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					id={data.id}
					product={ProductKeys.Loom}
					productSelected={false}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={data.url}
					title={enrichExcerpt(data.title)}
					icon={getIcon(data.__typename, data.title)}
					headerMetadataParts={[
						getSearchResultType(data.type as SearchClientResultType),
						creatorName && fg('loom-rovo-search') ? ['creator-name', creatorName] : null,
						[
							'video-count',
							videoCount != null ? formatMessage(messages.videoCount, { count: videoCount }) : null,
						],
					]}
					cloudId={cloudId}
					position={position}
					partial={data}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};
